import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

const RESERVED_ROOT_PATHS = new Set([
  'about', 'activity-log', 'admin', 'affiliate', 'ambassadors', 'api',
  'billing', 'blog', 'boats', 'careers', 'checkout', 'checklists', 'contact',
  'crewfinder', 'crewfinder-details', 'cruises', 'dashboard', 'emergency',
  'features', 'help', 'how-it-works', 'inventory', 'large-fleet', 'login',
  'mid-fleet', 'nautical-map', 'nautical-routes', 'notifications', 'onboard',
  'overview', 'pricing', 'privacy-policy', 'register', 'reminders',
  'reset-password', 'service-book-share', 'settings', 'small-fleet',
  'smart-yacht-management', 'starter', 'subscription-confirmation',
  'subscriptions', 'support', 'terms-and-conditions', 'thank-you',
  'unauthorized', 'upgrade', 'users', 'verify-email', 'why-yachtapp',
  'worklogs', 'yachtapp-solutions'
]);
const AMBASSADOR_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;

export const AFFILIATE_REFERRAL_STORAGE_KEY = 'yachtapp.affiliateReferral';
export const AFFILIATE_REFERRAL_COOKIE_NAME = 'yachtapp_affiliate_ref';

@Injectable({ providedIn: 'root' })
export class AffiliateReferralTrackingService {
  constructor(private readonly router: Router) {}

  init(): void {
    this.captureFromCurrentUrl();
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.captureFromCurrentUrl());
  }

  captureFromCurrentUrl(): string | null {
    const referralCode = this.readReferralCode(window.location.pathname, window.location.search);
    if (!referralCode) {
      return null;
    }

    window.localStorage?.setItem(AFFILIATE_REFERRAL_STORAGE_KEY, referralCode);
    document.cookie = `${AFFILIATE_REFERRAL_COOKIE_NAME}=${encodeURIComponent(referralCode)}; Path=/; Max-Age=${60 * 60 * 24 * 90}; SameSite=Lax`;
    return referralCode;
  }

  getStoredReferralCode(): string | null {
    return window.localStorage?.getItem(AFFILIATE_REFERRAL_STORAGE_KEY) || this.readReferralCookie();
  }

  private readReferralCode(pathname: string, search: string): string | null {
    const queryRef = new URLSearchParams(search).get('ref')?.trim();
    if (queryRef) {
      return queryRef;
    }

    const legacySlugMatch = pathname.match(/^\/ambassadors\/([^/?#]+)\/?$/);
    if (legacySlugMatch?.[1]) {
      return decodeURIComponent(legacySlugMatch[1]).trim() || null;
    }

    const shortSlugMatch = pathname.match(/^\/([^/?#]+)\/?$/);
    const shortSlug = shortSlugMatch?.[1] ? decodeURIComponent(shortSlugMatch[1]).trim().toLowerCase() : '';
    return AMBASSADOR_SLUG_PATTERN.test(shortSlug) && !RESERVED_ROOT_PATHS.has(shortSlug) ? shortSlug : null;
  }

  private readReferralCookie(): string | null {
    return document.cookie
      .split(';')
      .map(cookie => cookie.trim())
      .find(cookie => cookie.startsWith(`${AFFILIATE_REFERRAL_COOKIE_NAME}=`))
      ?.slice(AFFILIATE_REFERRAL_COOKIE_NAME.length + 1) ?? null;
  }
}
